/**
 * FACE ASSETS - Split SVG faces for DynamicFlapper
 * 
 * Each emotion has left and right halves that can be independently colored.
 * The SVGs are already split and ready to use.
 * 
 * Emotion Mapping:
 * - Joyful → joyful
 * - Focused → focused  
 * - Defeated → competitive
 * - Sad → reflective
 */

// Helper to generate unique IDs for SVG elements to avoid conflicts
let idCounter = 0;
function generateUniqueId(prefix) {
  return `${prefix}_${Date.now()}_${++idCounter}`;
}

/**
 * Make SVG IDs unique by replacing all IDs and their references
 */
function makeSVGUnique(svgString, emotion, side) {
  const uniqueId = generateUniqueId(`${emotion}_${side}`);
  const idMap = {};
  
  // Extract all IDs and create unique replacements
  let updated = svgString.replace(/id="([^"]+)"/g, (match, id) => {
    const newId = `${id}_${uniqueId}`;
    idMap[id] = newId;
    return `id="${newId}"`;
  });
  
  // Update all references (url(#id), mask="url(#id)", etc.)
  Object.entries(idMap).forEach(([oldId, newId]) => {
    const escapedOldId = oldId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    updated = updated.replace(new RegExp(`url\\(#${escapedOldId}\\)`, 'g'), `url(#${newId})`);
    updated = updated.replace(new RegExp(`"#${escapedOldId}"`, 'g'), `"#${newId}"`);
  });
  
  return updated;
}

// SVG file paths - these will be loaded dynamically
export const FACE_SVG_PATHS = {
  joyful: {
    left: '/faces/Face/Joy - Left.svg',
    right: '/faces/Face/Joy - Right.svg'
  },
  focused: {
    left: '/faces/Face/Focused - Left.svg',
    right: '/faces/Face/Focused - Right.svg'
  },
  competitive: {
    left: '/faces/Face/Defeated - Left.svg',
    right: '/faces/Face/Defeated - Face Right.svg'
  },
  reflective: {
    left: '/faces/Face/Sad - Face Left.svg',
    right: '/faces/Face/Sad - Face Right.svg'
  }
};

/**
 * Get the SVG path for a specific emotion and side
 */
export function getFaceSVGPath(emotion, side) {
  const emotionMap = {
    joyful: 'joyful',
    focused: 'focused',
    competitive: 'competitive',
    defeated: 'competitive', // Map defeated to competitive
    reflective: 'reflective',
    sad: 'reflective', // Map sad to reflective
    calm: 'joyful', // Fallback: calm maps to joyful
    resilient: 'focused' // Fallback: resilient maps to focused
  };
  
  const mappedEmotion = emotionMap[emotion] || 'joyful';
  const paths = FACE_SVG_PATHS[mappedEmotion];
  
  if (!paths) {
    return FACE_SVG_PATHS.joyful[side];
  }
  
  return paths[side] || paths.left;
}

/**
 * Load SVG content from file path
 * Returns a promise that resolves to the SVG string
 */
export async function loadFaceSVG(emotion, side) {
  const path = getFaceSVGPath(emotion, side);
  
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to load SVG: ${path}`);
    }
    const svgText = await response.text();
    return makeSVGUnique(svgText, emotion, side);
  } catch (error) {
    console.error(`Error loading face SVG for ${emotion} ${side}:`, error);
    // Fallback to joyful
    if (emotion !== 'joyful') {
      return loadFaceSVG('joyful', side);
    }
    return null;
  }
}
